<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          PropQual
        </h1>
        <a
          @click="$router.back()"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Back to PropQual Page
        </a>
      </div>
    </div>

    <loader v-if="!loaded || submittingOrder" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not have access to use this system.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col py-6 px-4">
      <h3 class="flex items-center leading-none px-4"><img src="/img/propqual-logo.png" width="32px" height="auto" class="mr-2" /> PropQual Registration</h3>
      <div class="flex flex-wrap mt-8">
        <div class="mb-6 px-4 w-full lg:w-2/3">
          <div class="border border-grey-light">

            <!-- STAGE TABS -->
            <div class="w-full flex text-sm mb-6">
              <div @click="stage = i" class="flex flex-col w-1/3" :class="{'border-l border-r': i === 1}" v-for="(stageName, i) in stages" :key="i">
                <div class="w-full h-full px-4 pt-3 pb-5 border-t-4" :class="[(i === stage ? 'border-t-4 border-propqual-orange' : 'border-transparent bg-grey-lighter'), (i === stage ? 'cursor-default' : 'cursor-pointer')]">
                  <div class="flex h-full items-center"><div class="w-8"><span class="w-6 h-6 rounded-full flex flex-col items-center justify-center text-white" :class="i === stage ? 'bg-propqual-orange' : 'bg-grey-dark'">{{ i + 1 }}</span></div> <div>{{ stageName }}</div></div>
                </div>
              </div>
            </div>

            <div class="bg-yellow-lighter flex p-4 mx-4 mb-4 rounded text-sm leading-normal" v-if="!isEmpty(errorBag.errors)">
              <i class="fas fa-exclamation-triangle mr-3 fa-2x pt-2"></i>
              <div class="flex-grow">
                <strong class="block mb-3 text-sm">There were problems with your order. Please resolve these issues and try again.</strong>
                <ul class="pl-6">
                  <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
                </ul>
              </div>
              <i class="fas fa-times text-xl ml-3 text-grey-darker hover:text-propqual-orange cursor-pointer" @click="errorBag.clear()"></i>
            </div>

            <div class="w-full text-sm mb-2 p-4">
              <!-- STAGE FORM -->
              <component :is="stageView" v-model="order" :courses="courses" :order="order" :registrants="registrants" :errorBag="errorBag" />

              <div class="w-full text-right" v-if="stage < 2">
                <button type="button" class="bg-primary py-3 px-20 border border-propqual-orange bg-propqual-orange-lightest hover:bg-propqual-orange text-propqual-orange hover:text-white focus:outline-none" @click="stage++">
                  {{ stage == 1 && order.additional_users.length === 0 ? 'Skip Step &gt;&gt;' : 'Next Step &gt;&gt;' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <PropQualOrderSummary :stage="stage" :registrants="registrants" :subtotal="subtotal" :vat="vat" :total="total" @submit="submitOrder" />

      </div>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import { isEmpty } from 'lodash'
import ErrorBag from '@/ErrorBag'
import PropQualOrderSummary from './propqual/PropQualOrderSummary.vue'
import PropQualRegistrationStage0 from './propqual/PropQualRegistrationStage0'
import PropQualRegistrationStage1 from './propqual/PropQualRegistrationStage1'
import PropQualRegistrationStage2 from './propqual/PropQualRegistrationStage2'
import { mapState } from 'vuex'
export default {
  components: {
    PropQualRegistrationStage0,
    PropQualRegistrationStage1,
    PropQualRegistrationStage2,
    PropQualOrderSummary
  },
  data () {
    return {
      loaded: false,
      officeAccess: false,
      order: {
        first_name: null,
        last_name: null,
        company_name: null,
        job_title: null,
        email: null,
        course_id: null,
        additional_users: []
      },
      registrants: [],
      stage: 0,
      stages: [
        'Your Details',
        'Additional Users',
        'Review & Payment'
      ],
      courses: null,
      subtotal: '0.00',
      vat: '0.00',
      total: '0.00',
      errorBag: new ErrorBag(),
      submittingOrder: false,
      orderResponse: null
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    stageView () {
      return `prop-qual-registration-stage${this.stage}`
    }
  },
  watch: {
    order: {
      handler () {
        this.setRegistrants()
        this.calculateTotals()
      },
      deep: true
    }
  },
  created () {
    Promise.all([
      this.loadCourses()
    ]).then(() => {
      this.order.first_name = this.profile.first_name
      this.order.last_name = this.profile.surname
      this.order.email = this.profile.email
      this.order.company_name = this.selectedOffice.client_name
      this.loaded = true
    })
  },
  methods: {
    isEmpty,
    loadCourses () {
      return api.get('propqual/courses', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.courses = response.data.courses
        this.officeAccess = response.data.access
      })
    },
    courseName (id) {
      let course = this.courses.find(c => c.id === id)
      return course.name
    },
    courseFee (id) {
      let course = this.courses.find(c => c.id === id)
      return course.admin_fee
    },
    setRegistrants () {
      let registrants = []
      if (this.order.course_id) {
        registrants.push({
          first_name: this.order.first_name ? this.order.first_name : '',
          last_name: this.order.last_name ? this.order.last_name : '',
          email: this.order.email,
          course_name: this.courseName(this.order.course_id),
          course_fee: this.courseFee(this.order.course_id)
        })
      }
      for (let i = 0; i < this.order.additional_users.length; i++) {
        let user = this.order.additional_users[i]
        if (user.course_id) {
          registrants.push({
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            course_name: this.courseName(user.course_id),
            course_fee: this.courseFee(user.course_id)
          })
        }
      }
      this.registrants = registrants
    },
    calculateTotals () {
      let subtotal = 0
      for (let i = 0; i < this.registrants.length; i++) {
        let registrant = this.registrants[i]
        subtotal += registrant.course_fee
      }
      subtotal = parseFloat(subtotal)
      this.subtotal = subtotal.toFixed(2)
      this.vat = (subtotal * 0.2).toFixed(2)
      this.total = (subtotal * 1.2).toFixed(2)
    },
    submitOrder () {
      this.errorBag.clear()
      this.order.office_id = this.selectedOffice.id
      this.submittingOrder = true

      api.post('propqual/orders', this.order).then((response) => {
        this.orderResponse = response.data
        if (response.data.checkout_url) {
          window.location = response.data.checkout_url
        } else {
          this.submittingOrder = false
        }
      }, (errors) => {
        this.submittingOrder = false
        this.errorBag.store(errors.response.data)
      })
    }
  }
}
</script>

<style lang="scss">
input[type="radio"] {
  display: inline-block;
}
</style>
